import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Loader2, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import SearchableSelect from "./SearchableSelect";
import DegerKaybiParcalariSection from "./DegerKaybiParcalariSection";
import { fkId } from "../lib/fkId";
import {
    PANEL_MODAL_SHELL,
    PANEL_SECTION,
} from "../lib/panelFormStyles";

function money(value) {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n);
}

function Stat({ label, value, accent }) {
    return (
        <div className="px-3 py-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">
                {label}
            </div>
            <div
                className={
                    "text-sm font-semibold font-mono-jb mt-0.5 truncate " +
                    (accent ? "text-[#F3E5AB]" : "text-slate-100")
                }
            >
                {value}
            </div>
        </div>
    );
}

function InfoGrid({ rows }) {
    return (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
            {rows.map(({ label, value }) => (
                <div key={label} className="min-w-0">
                    <dt className="text-[10px] uppercase tracking-wider text-slate-500">
                        {label}
                    </dt>
                    <dd className="text-slate-100 font-medium mt-0.5 truncate">
                        {value ?? "—"}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

export default function DegerKaybiFormModal({
    open,
    title,
    initial,
    onClose,
    onSaved,
}) {
    const isEdit = !!initial?.id;
    const [sigortaDosyasi, setSigortaDosyasi] = useState(null);
    const [sigortaPreview, setSigortaPreview] = useState(null);
    const [record, setRecord] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const loadRecord = useCallback(async (id) => {
        if (!id) {
            setRecord(null);
            return;
        }
        try {
            setRecord(await crud.retrieve("deger-kaybi", id));
        } catch {
            setRecord(initial || null);
        }
    }, [initial]);

    const loadSigortaPreview = useCallback(async (sdId) => {
        if (!sdId) {
            setSigortaPreview(null);
            return;
        }
        setLoadingPreview(true);
        try {
            setSigortaPreview(await crud.retrieve("sigorta-dosyalari", sdId));
        } catch {
            setSigortaPreview(null);
        } finally {
            setLoadingPreview(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setErr(null);
        const sdId =
            fkId(initial?.sigorta_dosyasi) ??
            fkId(initial?.sigorta_dosya) ??
            null;
        setSigortaDosyasi(sdId);
        setRecord(initial || null);
        if (sdId) loadSigortaPreview(sdId);
        else setSigortaPreview(null);
        if (isEdit && initial?.id) loadRecord(initial.id);
    }, [open, initial, isEdit, loadSigortaPreview, loadRecord]);

    useEffect(() => {
        if (!open) return;
        if (sigortaDosyasi) loadSigortaPreview(sigortaDosyasi);
        else setSigortaPreview(null);
    }, [sigortaDosyasi, open, loadSigortaPreview]);

    if (!open) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!sigortaDosyasi) {
            setErr("Sigorta dosyası seçin.");
            return;
        }
        setBusy(true);
        setErr(null);
        try {
            if (isEdit) {
                await crud.update("deger-kaybi", initial.id, {
                    sigorta_dosyasi: sigortaDosyasi,
                    hesaplamayi_tamamla: true,
                });
                toast.success(
                    "Kayıt güncellendi. Parça varsa hesaplama maili gönderildi.",
                );
            } else {
                await crud.create("deger-kaybi", {
                    sigorta_dosyasi: sigortaDosyasi,
                });
                toast.success("Değer kaybı oluşturuldu.");
            }
            onSaved?.();
        } catch (e) {
            const data = e?.response?.data;
            const sdErr = data?.sigorta_dosyasi;
            const msg = Array.isArray(sdErr)
                ? sdErr.join(" ")
                : typeof sdErr === "string"
                  ? sdErr
                  : data?.detail ||
                    (typeof data === "object" ? null : String(data)) ||
                    e?.message ||
                    "Kayıt başarısız.";
            setErr(msg || "Kayıt başarısız.");
        } finally {
            setBusy(false);
        }
    }

    const muvekkilLabel =
        sigortaPreview?.muvekkil_label ||
        (sigortaPreview?.muvekkil && typeof sigortaPreview.muvekkil === "object"
            ? [sigortaPreview.muvekkil.ad, sigortaPreview.muvekkil.soyad]
                  .filter(Boolean)
                  .join(" ")
            : null);

    const calc = record || initial;
    const rayic =
        calc?.hasar_tutari ?? sigortaPreview?.arac_rayic_degeri ?? null;

    const modal = (
        <div
            className="fixed inset-0 z-[80] overflow-y-auto"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="fixed inset-0 bg-black/75 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="relative min-h-full flex items-start justify-center px-4 py-8">
                <div className={PANEL_MODAL_SHELL}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/35 flex items-center justify-center">
                                <TrendingDown className="h-4 w-4 text-[#F3E5AB]" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    {isEdit ? "Düzenle" : "Yeni kayıt"}
                                </div>
                                <div className="font-display text-lg text-slate-100">
                                    {title}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-5 space-y-4">
                        {err && (
                            <div className="text-sm text-rose-300 bg-rose-500/15 border border-rose-500/30 rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}

                        <form id="dk-form" onSubmit={handleSubmit} className="space-y-4">
                            <div className={`${PANEL_SECTION} p-4 space-y-3`}>
                                <label className="block text-xs font-medium text-slate-400">
                                    Sigorta dosyası
                                </label>
                                <SearchableSelect
                                    value={sigortaDosyasi}
                                    onChange={setSigortaDosyasi}
                                    resource="sigorta-dosyalari"
                                    placeholder="Dosya no, plaka veya müvekkil..."
                                    testId="dk-sigorta-dosya"
                                    listParams={
                                        isEdit ? undefined : { without_deger_kaybi: "1" }
                                    }
                                />
                                {!isEdit && (
                                    <p className="text-[11px] text-slate-500">
                                        Listede yalnızca henüz değer kaybı açılmamış
                                        dosyalar gösterilir. Bu dosya için kayıt varsa
                                        listeden açıp düzenleyin.
                                    </p>
                                )}
                            </div>

                            {loadingPreview && (
                                <div className="flex items-center gap-2 text-sm text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Yükleniyor...
                                </div>
                            )}

                            {sigortaPreview && !loadingPreview && (
                                <div className={`${PANEL_SECTION} p-4`}>
                                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">
                                        Dosya özeti
                                    </p>
                                    <InfoGrid
                                        rows={[
                                            {
                                                label: "Dosya no",
                                                value: sigortaPreview.dosya_no,
                                            },
                                            {
                                                label: "Plaka",
                                                value: sigortaPreview.plaka,
                                            },
                                            { label: "Müvekkil", value: muvekkilLabel },
                                            {
                                                label: "Araç",
                                                value: [
                                                    sigortaPreview.marka,
                                                    sigortaPreview.model,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" "),
                                            },
                                            {
                                                label: "Km",
                                                value:
                                                    sigortaPreview.km != null
                                                        ? `${Number(sigortaPreview.km).toLocaleString("tr-TR")} km`
                                                        : "—",
                                            },
                                            {
                                                label: "Rayiç",
                                                value: money(
                                                    sigortaPreview.arac_rayic_degeri,
                                                ),
                                            },
                                        ]}
                                    />
                                </div>
                            )}

                            {isEdit && calc && (
                                <div className={`${PANEL_SECTION} p-1`}>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/10">
                                        <Stat
                                            label="Rayiç"
                                            value={money(rayic)}
                                        />
                                        <Stat
                                            label="Baz DK"
                                            value={money(calc.hesaplanan_baz_deger_kaybi)}
                                        />
                                        <Stat
                                            label="Gerçek DK"
                                            value={money(
                                                calc.hesaplanan_real_deger_kaybi,
                                            )}
                                            accent
                                        />
                                        <Stat
                                            label="Alınan"
                                            value={money(calc.alinan_tutar)}
                                        />
                                    </div>
                                </div>
                            )}
                        </form>

                        {isEdit && initial?.id && (
                            <DegerKaybiParcalariSection
                                parentId={initial.id}
                                hideSummary
                                embedded
                                onSummaryChange={(rec) => setRecord(rec)}
                            />
                        )}
                    </div>

                    <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-white/10 bg-[#0B0F19]/90">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-9 px-4 rounded-md text-sm border border-white/15 text-slate-300 hover:bg-white/5"
                        >
                            Kapat
                        </button>
                        <button
                            type="submit"
                            form="dk-form"
                            disabled={busy}
                            className="btn-gold h-9 px-5 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Kaydet
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
